import { cn } from '@tempo-io/sui-class-utils';
import { useLayoutEffect, useRef, useState } from 'react';
import { formatSecondsToHours } from '@/lib/utils/common/formatSecondsToHours';

const LABEL_PADDING = 8;
const LABEL_GAP = 4;

export const RatioBar = ({
  ratio,
  seconds,
  cap,
  backgroundColor,
  textColor = 'text-text-neutral',
}: {
  ratio: number;
  seconds: number;
  cap: number;
  backgroundColor: string;
  textColor?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [showLabelInside, setShowLabelInside] = useState(false);

  const cappedRatio = Math.min(ratio, cap);
  const ratioToUse = seconds > 0 ? Math.max(cappedRatio, 0.03) : 0.03;
  const exceeds = ratio > cap;
  const stripeClassName =
    'bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(255,255,255,0.3)_2px,rgba(255,255,255,0.3)_4px)]';

  const formattedLabel = formatSecondsToHours(seconds);

  useLayoutEffect(() => {
    if (containerRef.current && labelRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const barWidth = containerWidth * ratioToUse;
      const labelWidth = labelRef.current.offsetWidth;

      const fitsOutside = barWidth + labelWidth + LABEL_GAP <= containerWidth;
      const fitsInside = barWidth >= labelWidth + LABEL_PADDING * 2;

      setShowLabelInside(!fitsOutside && fitsInside);
    }
  }, [ratioToUse]);

  const barWidthPercent = `${ratioToUse * 100}%`;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-3.5 bg-bg-neutral rounded-lg overflow-hidden"
    >
      <div className={cn('h-3.5 relative', backgroundColor)} style={{ width: barWidthPercent }}>
        {exceeds && <div className={cn('absolute top-0 right-0 h-full w-2', stripeClassName)} />}
      </div>

      {showLabelInside && (
        <span
          className={cn(
            'text-xs font-bold absolute inset-0 flex items-center justify-center whitespace-nowrap',
            textColor,
          )}
        >
          {formattedLabel}
        </span>
      )}

      <span
        ref={labelRef}
        title={formattedLabel}
        className={cn(
          'text-xs font-bold absolute top-0 h-3.5 flex items-center pl-1 whitespace-nowrap text-text-neutral-primary',
          showLabelInside && 'opacity-0 pointer-events-none',
        )}
        style={{ left: barWidthPercent }}
      >
        {formattedLabel}
      </span>
    </div>
  );
};
